import { WeekAvailability } from "./AvailabilityChart";
import { WorkshopJob } from "../../types/garage/atelier";
import { AvailabilityBlock, ZoneType, ZONE_COLORS } from "@store/dispoAtelierStore";
import { getMonday, toISO } from "@store/disponibiliteStore";

/** Zone types that represent workshop capacity */
const CAPACITY_ZONES: ZoneType[] = [
  "mecanique",
  "carrosserie",
  "peinture",
  "diagnostique",
  "service_rapide",
  "campagnes",
];

/** Get Mon–Fri dates for a week starting from a given Monday */
function getWorkWeekDates(monday: Date): string[] {
  return Array.from({ length: 5 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return toISO(d);
  });
}

/** Get the day-of-week index (0=Mon … 6=Sun) for a date string */
function dayOfWeekIndex(dateStr: string): number {
  const d = new Date(dateStr + "T00:00:00");
  const jsDay = d.getDay(); // 0=Sun
  return jsDay === 0 ? 6 : jsDay - 1;
}

/** Sum block hours for a specific zone type on a specific date */
function getZoneHoursForDate(
  blocks: AvailabilityBlock[],
  zone: ZoneType,
  dateStr: string,
): number {
  const dow = dayOfWeekIndex(dateStr);
  let hours = 0;
  for (const b of blocks) {
    if (b.zoneType !== zone) continue;
    const matches =
      b.date === dateStr ||
      (b.recurring && b.dayOfWeek === dow);
    if (matches) hours += b.durationHours;
  }
  return hours;
}

/** Format a date range string dd/mm – dd/mm */
function formatRange(dates: string[]): string {
  const fmt = (d: string) => {
    const [, m, day] = d.split("-");
    return `${day}/${m}`;
  };
  return `${fmt(dates[0])} - ${fmt(dates[dates.length - 1])}`;
}

export function computeWeeklyAvailability(
  selectedDate: string,
  jobs: WorkshopJob[],
  blocks: AvailabilityBlock[],
): WeekAvailability[] {
  const monday = getMonday(selectedDate);
  const weeks: WeekAvailability[] = [];

  for (let w = 0; w < 3; w++) {
    const weekMonday = new Date(monday);
    weekMonday.setDate(monday.getDate() + w * 7);
    const dates = getWorkWeekDates(weekMonday);

    const weekJobs = jobs.filter((j) => dates.includes(j.date));

    const bars = CAPACITY_ZONES
      .map((zone) => {
        const { text: color, label } = ZONE_COLORS[zone];
        let zoneHours = 0;
        for (const date of dates) {
          zoneHours += getZoneHoursForDate(blocks, zone, date);
        }
        if (zoneHours === 0) return null;

        // Match jobs: service_rapide ← "autre" specialty, others ← same name
        const matchSpecialty = zone === "service_rapide" ? "autre" : zone;
        const occupied = weekJobs
          .filter((j) => j.specialty === matchSpecialty)
          .reduce((sum, j) => sum + j.durationHours, 0);

        const freePct = Math.max(0, Math.round(((zoneHours - occupied) / zoneHours) * 100));
        return { color, height: freePct, label };
      })
      .filter((b): b is { color: string; height: number; label: string } => b !== null);

    weeks.push({ dateRange: formatRange(dates), bars });
  }

  return weeks;
}
